// this code is for the chunks and micro chunks frontend page in teaching flow as displayed to students

import React, { useState, useEffect } from 'react';
import { useLocation, useNavigate } from 'react-router-dom';
import { useLanguage } from '../contexts/languagecontext';
import ChatInput from './chatinput';
import ApiService from '../services/api';
import LearningPage from './learningpage';
import { API_BASE, authFetch } from '../services/api.js';
import CustomSelect from '../components/CustomSelect';

const EnhancedLearningInterface = () => {
  const location = useLocation();
  const navigate = useNavigate();
  
  // Get data passed from TeachingMode
  const passedData = location.state || {};
  const {
    chunk: passedChunk,
    selectedSubject: passedSubject,
    selectedChapter: passedChapter,
    selectedTopic: passedTopic,
    selectedSubtopic: passedSubtopic,
    language: passedLanguage,
    viewAllChunks: passedViewAllChunks
  } = passedData;

  // Language context
  const { currentLanguage, switchLanguage } = useLanguage();

  // Page states
  const [currentPage, setCurrentPage] = useState('chunks');
  
  // Real data states
  const [chunks, setChunks] = useState([]);
  const [loading, setLoading] = useState(false);
  
  // Selection states
  const [selectedTopic, setSelectedTopic] = useState(passedTopic || null);
  const [selectedSubtopic, setSelectedSubtopic] = useState(passedSubtopic || null);
  const [expandedChunks, setExpandedChunks] = useState(new Set());
  const [expandAll, setExpandAll] = useState(false);
  
  // Learning page states
  const [currentChunk, setCurrentChunk] = useState(null);
  const [currentMicroChunk, setCurrentMicroChunk] = useState(null);
  const [currentMicroIndex, setCurrentMicroIndex] = useState(0);
  const [showExplanation, setShowExplanation] = useState(false);
  const [showExample, setShowExample] = useState(false);
  const [showChat, setShowChat] = useState(false);
  const [chatMessages, setChatMessages] = useState([]);
  const [chatInput, setChatInput] = useState('');
  
  
  
  // Load initial data
  useEffect(() => {
    if (passedTopic) {
      loadChunksForSelection();
    }
  }, []);

  // Reload content when language changes
  useEffect(() => {
    if (passedTopic) {
      loadChunksForSelection();
    }
    // Also refresh current micro chunk if in learning mode
    if (currentMicroChunk && currentPage === 'learning') {
      loadMicroChunkForLanguage();
    }
  }, [currentLanguage, passedTopic, , passedSubject, passedChapter, passedSubtopic]);  // 15aug2025

  const loadChunksForSelection = async () => {
    if (!passedSubject || !passedChapter || !passedTopic) return;
    console.log("Loading with language:", currentLanguage); // Add this
    
    setLoading(true);
    try {
      const params = new URLSearchParams({
        subject_id: passedSubject.subject_id,
        chapter_id: passedChapter.chapter_id,
        topic_id: passedTopic.topic_id
      });
      
      // If not viewing all chunks and have a specific subtopic, filter by subtopic
      if (!passedViewAllChunks && passedSubtopic) {
        params.append('subtopic_id', passedSubtopic.subtopic_id);
      }
      
      console.log("Final URL:", `${API_BASE}/teach/chunks-with-translation?${params}&sort_by_subtopic=true&language=${currentLanguage}`); // Add this
      console.log('Loading chunks with params:', params.toString());
      
      const userData = JSON.parse(localStorage.getItem('userData'));
      const studentId = userData?.student?.student_id;
      const response = await authFetch(`${API_BASE}/teach/chunks-with-translation?${params}&sort_by_subtopic=true&language=${currentLanguage}&student_id=${studentId}`);
      if (!response.ok) {
        console.error('Chunks API error:', response.status, await response.text());
        throw new Error(`Failed to fetch chunks: ${response.status}`);
      }
      const chunksData = await response.json();
      console.log('Loaded chunks data:', chunksData);
      
      // Transform chunks to include micro_chunks
      const chunksWithMicro = await Promise.all(
        chunksData.map(async (chunk) => {
          try {
            const microParams = new URLSearchParams({
              chunk_id: chunk.chunk_id
            });
            console.log('Loading micro chunks for chunk:', chunk.chunk_id);
            
            const userData = JSON.parse(localStorage.getItem('userData'));
            const studentId = userData?.student?.student_id;
            const microResponse = await authFetch(`${API_BASE}/teach/micro-chunks-with-translation/${chunk.chunk_id}?language=${currentLanguage}&student_id=${studentId}`);
            const microChunks = microResponse.ok ? await microResponse.json() : [];
            console.log('Loaded micro chunks:', microChunks);
            
            return {
              ...chunk,
              title: chunk.chunk_text ? chunk.chunk_text.substring(0, 100) : `Chunk ${chunk.chunk_id}`,
              status: 'not_started',
              micro_chunks: microChunks.map(mc => ({
                ...mc,
                title: mc.content || 'No content',
                status: 'not_started'
              }))
            };
          } catch (error) {
            console.error('Error loading micro chunks for chunk', chunk.chunk_id, ':', error);
            return {
              ...chunk,
              title: chunk.chunk_text ? chunk.chunk_text.substring(0, 100) : `Chunk ${chunk.chunk_id}`,
              status: 'not_started',
              micro_chunks: []
            };
          }
        })
      );
      
      console.log('Final chunks with micro chunks:', chunksWithMicro);
      setChunks(chunksWithMicro);
    } catch (error) {
      console.error('Failed to load chunks:', error);
      setChunks([]);
    }
    setLoading(false);
  };

  const loadMicroChunkForLanguage = async () => {
    if (!currentMicroChunk) return;
    
    try {
      const userData = JSON.parse(localStorage.getItem('userData'));
      const studentId = userData?.student?.student_id;
      const response = await authFetch(`${API_BASE}/micro-chunks/${currentMicroChunk.micro_chunk_id}?language=${currentLanguage}&student_id=${studentId}`);
      if (response.ok) {
        const updatedMicroChunk = await response.json();
        setCurrentMicroChunk(updatedMicroChunk);
        
        // Clear explanations and examples as they are language-specific
        setShowExplanation(false);
        setShowExample(false);
        //setExplanationContent('');
        //setExampleContent('');
        //setExplanationList([]);
        //setExampleList([]);
      }
    } catch (error) {
      console.error('Error loading micro chunk for language:', error);
    }
  };

  const handleExpandAll = () => {
    if (expandAll) {
      setExpandedChunks(new Set());
    } else {
      setExpandedChunks(new Set(chunks.map(chunk => chunk.chunk_id)));
    }
    setExpandAll(!expandAll);
  };

  const toggleChunkExpansion = (chunkId) => {
    const newExpanded = new Set(expandedChunks);
    if (newExpanded.has(chunkId)) {
      newExpanded.delete(chunkId);
    } else {
      newExpanded.add(chunkId);
    }
    setExpandedChunks(newExpanded);
  };

  const startChunkLearning = (chunk) => {
    setCurrentChunk(chunk);
    if (chunk.micro_chunks && chunk.micro_chunks.length > 0) {
      setCurrentMicroChunk(chunk.micro_chunks[0]);
      setCurrentMicroIndex(0);
    }
    setCurrentPage('learning');
  };

  const goToNextMicroChunk = () => {
    if (currentMicroIndex < currentChunk.micro_chunks.length - 1) {
      const nextIndex = currentMicroIndex + 1;
      setCurrentMicroIndex(nextIndex);
      setCurrentMicroChunk(currentChunk.micro_chunks[nextIndex]);
      
      // Clear explanations and examples when moving to next micro chunk
      setShowExplanation(false);
     setShowExample(false);
      
    } else {
      // Handle completion - go back to chunks page
      console.log('Chunk completed! Going back to chunks...');
      alert('🎉 Congratulations! You have completed this chunk!');
      setCurrentPage('chunks');
      setCurrentChunk(null);
      setCurrentMicroChunk(null);
      setCurrentMicroIndex(0);
      setShowExplanation(false);
      setShowExample(false);
      setShowChat(false);
    }

  };

  
  const goToPreviousMicroChunk = () => {
    if (currentMicroIndex > 0) {
      const prevIndex = currentMicroIndex - 1;
      setCurrentMicroIndex(prevIndex);
      setCurrentMicroChunk(currentChunk.micro_chunks[prevIndex]);
      
      // Clear explanations and examples when moving to previous micro chunk
      setShowExplanation(false);
      setShowExample(false);
      //setExplanationContent('');
      //setExampleContent('');
    }
  };

  const backToChunks = () => {
    setCurrentPage('chunks');
    setCurrentChunk(null);
    setCurrentMicroChunk(null);
    setCurrentMicroIndex(0);
    setShowExplanation(false);
    setShowExample(false);
    setShowChat(false);
  };

  
  

  

  
  

  const sendChatMessage = () => {
    if (!chatInput.trim()) return;
    
    const newMessage = {
      id: Date.now(),
      text: chatInput,
      sender: 'student',
      timestamp: new Date()
    };
    
    setChatMessages(prev => [...prev, newMessage]);
    setChatInput('');
    
    // Simulate AI response in current language
    setTimeout(() => {
      const aiResponse = {
        id: Date.now() + 1,
        text: currentLanguage === 'hi' 
          ? "यह एक बेहतरीन सवाल है! मैं समझाता हूं... [यह AI द्वारा जेनरेट किया गया उत्तर होगा]"
          : "That's a great question! Let me explain... [This would be AI generated response]",
        sender: 'ai',
        timestamp: new Date()
      };
      setChatMessages(prev => [...prev, aiResponse]);
    }, 1000);
  };

  // Chunks Page Component
  const ChunksPage = () => (
    <div style={{
      background: 'white',
      borderRadius: '12px',
      boxShadow: '0 2px 10px rgba(0, 0, 0, 0.1)',
      overflow: 'hidden'
    }}>
      {/* Header with Language Selector */}
      <div style={{
        background: 'linear-gradient(135deg, #667eea 0%, #764ba2 100%)',
        padding: '8px 12px',
        color: 'white',
        display: 'flex',
        justifyContent: 'space-around',
        alignItems: 'center',
        flexWrap: 'wrap',
        gap: '20px',
        width: '100%',  // ADDED: full width
        fontSize: '0.8rem' // Smaller base font
      }}>
        <div style={{flex: '1', minWidth: '120px'}}>
          <h1 style={{margin: 0, fontSize: '0.9rem', fontWeight: '600'}}>
            📚 {selectedTopic?.name }
          </h1>
          {selectedSubtopic && (
            <p style={{margin: '2px 0 0 0', opacity: 0.9, fontSize: '0.75rem'}}> {/* Smaller subtopic font */}
               📖 {selectedSubtopic.name}
            </p>
          )}     

        </div>
        
        <div style={{display: 'flex', gap: '4px', alignItems: 'center', flexWrap: 'wrap'}}>
          {/* Language Selection - No "Language:" text */}
          <CustomSelect
            value={currentLanguage}
            onChange={(e) => switchLanguage(e.target.value)}
            options={[
              { value: 'en', label: '🇺🇸 English' },
              { value: 'hi', label: '🇮🇳 Hindi' }
            ]}
            placeholder="Select Language"
          />
          

          <button onClick={handleExpandAll} style={{
            background: 'rgba(255, 255, 255, 0.2)',
            color: 'white',
            border: '1px solid rgba(255, 255, 255, 0.3)',
            padding: '4px 8px',
            borderRadius: '4px',
            cursor: 'pointer',
            fontSize: '0.75rem',
            flex: '0 0 auto'  // ADDED: don't grow/shrink
          }}>
            {expandAll ? 'Collapse All' : 'Expand All'}
          </button>
          <button 
            onClick={() => navigate('/topics-subtopics', { 
              state: { 
                selectedSubject: passedSubject,
                selectedChapter: passedChapter,
                language: currentLanguage
              }
            })}
            style={{
              background: 'rgba(255, 255, 255, 0.2)',
              color: 'white',
              border: '1px solid rgba(255, 255, 255, 0.3)',
              padding: '4px 8px',
              borderRadius: '4px',
              cursor: 'pointer',
              fontSize: '0.75rem',
              flex: '0 0 auto'  // ADDED: don't grow/shrink
            }}
          >
            ← Topics
          </button>
        </div>
      </div>

      {/* Chunks List */}
      <div>
        {loading ? (
          <div style={{padding: '40px', textAlign: 'center'}}>
            <div>Loading chunks...</div>
          </div>
        ) : chunks.length === 0 ? (
          <div style={{padding: '40px', textAlign: 'center', color: '#6b7280'}}>
            No chunks found for this topic
          </div>
        ) : (
          chunks.map(chunk => (
            <div key={chunk.chunk_id}>
              <div 
                style={{
                  display: 'flex',
                  alignItems: 'center',
                  padding: '15px 10px',  // CHANGED: reduced left padding from 20px to 10px
                  cursor: 'pointer',
                  gap: '12px',
                  borderBottom: '1px solid #e5e7eb',
                  backgroundColor: expandedChunks.has(chunk.chunk_id) ? '#f9fafb' : 'white',
                  transition: 'background-color 0.2s ease'
                }}
                onClick={() => toggleChunkExpansion(chunk.chunk_id)}
              >
                <span style={{
                  width: '12px',
                  height: '12px',
                  borderRadius: '50%',
                  backgroundColor: '#E5E7EB'
                }}></span>
                <span style={{
                  flex: 1,
                  fontWeight: '600',
                  color: '#374151',
                  fontSize: '16px',
                  textAlign: 'left'
                }}>
                  {chunk.title}
                </span>
                <span
                  onClick={(e) => {
                    e.stopPropagation();
                    startChunkLearning(chunk);
                  }}
                  style={{
                    color: '#667eea',
                    fontWeight: 'bold',
                    cursor: 'pointer',
                    fontSize: '18px',
                    marginRight: '10px',
                    minWidth: '20px',
                    display: 'flex',
                    alignItems: 'center',
                    justifyContent: 'center' 
                  }}
                >
                  ▶
                  
                </span>
                {/* <span style={{color: '#6b7280', fontSize: '18px', fontWeight: 'bold',cursor: 'pointer'}}> 
                  {expandedChunks.has(chunk.chunk_id) ? '▼' : '▶'}
                </span> */}
              </div>

              {/* Micro Chunks - Show when expanded */}
              {expandedChunks.has(chunk.chunk_id) && (
                <div style={{backgroundColor: '#f9fafb'}}>
                  {chunk.micro_chunks.length === 0 ? (
                    <div style={{
                      padding: '15px 10px',
                      color: '#6b7280',
                      fontStyle: 'italic'
                    }}>
                      No Points available
                    </div>
                  ) : (
                    chunk.micro_chunks.map((microChunk, index) => (
                      <div 
                        key={microChunk.micro_chunk_id}
                        style={{
                          display: 'flex',
                          alignItems: 'flex-start',  // CHANGED: to handle multi-line text
                          padding: '12px 10px',  // CHANGED: removed all left indentation
                          gap: '12px',
                          borderBottom: '1px solid #e5e7eb',
                          cursor: 'pointer',
                          transition: 'background-color 0.2s ease'
                        }}
                        onClick={() => {
                          setCurrentChunk(chunk);
                          setCurrentMicroChunk(microChunk);
                          setCurrentMicroIndex(index);
                          setCurrentPage('learning');
                        }}
                        onMouseEnter={(e) => e.target.style.backgroundColor = '#f3f4f6'}
                        onMouseLeave={(e) => e.target.style.backgroundColor = 'transparent'}
                      >
                        {/* <span style={{
                          width: '8px',
                          height: '8px',
                          borderRadius: '50%',
                          backgroundColor: '#E5E7EB',
                          marginTop: '6px',  // ADDED: align with first line of text
                          flexShrink: 0  // ADDED: prevent shrinking
                        }}></span> */}
                        <span style={{
                          flex: 1,
                          color: '#4b5563',
                          fontSize: '14px',
                          textAlign: 'left',
                          lineHeight: '1.4',  // ADDED: better line spacing
                          whiteSpace: 'normal',  // ADDED: allow text wrapping
                          wordBreak: 'break-word'  // ADDED: break long words
                        }}>
                          🔹 {microChunk.content|| 'No content'}
                        </span>
                        <span style={{
                          color: '#667eea',
                          fontSize: '16px',
                          fontWeight: '500',
                          fontWeight: 'bold',  // ADDED: make it more visible
                          flexShrink: 0,  // ADDED: prevent shrinking
                          marginTop: '2px'  // ADDED: slight alignment
                        }}>
                          ➤
                        </span>
                      </div>
                    ))
                  )}
                </div>
              )}
            </div>
          ))
        )}
      </div>
    </div>
  );

  // Replace the entire const LearningPage = () => ( ... ); section with:
  const renderLearningPage = () => (
    <LearningPage 
      currentChunk={currentChunk}
      currentMicroChunk={currentMicroChunk}
      currentMicroIndex={currentMicroIndex}
      passedSubject={passedSubject}
      passedChapter={passedChapter}
      passedTopic={passedTopic}
      passedSubtopic={passedSubtopic}
      goToPreviousMicroChunk={goToPreviousMicroChunk}
      goToNextMicroChunk={goToNextMicroChunk}
      backToChunks={backToChunks}
      showExplanation={showExplanation}
      setShowExplanation={setShowExplanation}
      //explanationContent={explanationContent}
      //explanationList={explanationList}
      //currentExplanationIndex={currentExplanationIndex}
      //goToPreviousExplanation={goToPreviousExplanation}
      //goToNextExplanation={goToNextExplanation}
      showExample={showExample}
      setShowExample={setShowExample}
      //exampleContent={exampleContent}
      //exampleList={exampleList}
      //currentExampleIndex={currentExampleIndex}
      //goToPreviousExample={goToPreviousExample}
      //goToNextExample={goToNextExample}
      showChat={showChat}
      setShowChat={setShowChat}
    />
  );

  return (
    <div style={{ minHeight: '100vh', background: '#f8fafc', padding: '20px' }}>
      {currentPage === 'chunks' && <ChunksPage />}
      {currentPage === 'learning' && renderLearningPage()}
    </div>
  );
};  

export default EnhancedLearningInterface;